import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useBusinessUnit } from "../../context/BusinessUnitContext";
import {
  Box,
  Button,
  ButtonGroup,
  Grid,
  Center,
  SimpleGrid,
  Spinner,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getDashboardStatistics } from "../../api/statisticsApi";
import { getDashboardKpis } from "../../api/partnersApi";
import {
  DASHBOARD_PERSON_TYPES,
  buildFallbackMonths,
  buildMonthRange,
  buildPersonOptions,
  buildPilotChartData,
  buildPilotDetailRows,
  buildRankings,
  buildSeriesFromStatistics,
  buildWeekBuckets,
  buildWeekSeriesFromStatistics,
  buildWeekRangeForMonth,
  buildMonthKey,
  getWeekRange,
  safeNumber,
  sumSeries,
} from "../../components/Portal/Dashboard/dashboardUtils";
import DashboardFilters from "../../components/Portal/Dashboard/DashboardFilters";
import ModuleVolumeCard from "../../components/Portal/Dashboard/ModuleVolumeCard";
import MyTasksWidget from "../../components/Portal/Dashboard/MyTasksWidget";
import PilotGroupedChartCard from "../../components/Portal/Dashboard/PilotGroupedChartCard";
import PilotDetailTableCard from "../../components/Portal/Dashboard/PilotDetailTableCard";
import RankingPodiumCard from "../../components/Portal/Dashboard/RankingPodiumCard";
import { PageHeader, Panel, KpiStrip, KpiCard, EmptyState } from "../../components/Portal/ui";
import {
  convertLegacyRole,
  convertLegacyRoleWithContext,
  isBUAdmin,
  isCompanyAdmin,
  isManagerRole,
  isRecruiterRole,
  isSalesRole,
} from "../../constants/roles";

const getCurrentUserId = (user) => {
  if (user?.id !== undefined && user?.id !== null) return String(user.id);
  if (user?.userId !== undefined && user?.userId !== null) return String(user.userId);
  if (user?.user_id !== undefined && user?.user_id !== null) return String(user.user_id);
  return null;
};

const Dashboard = () => {
  const { user } = useAuth();
  const toast = useToast();
  const { t, i18n } = useTranslation("portal");
  const navigate = useNavigate();
  const location = useLocation();
  const {
    businessUnits,
    selectedBusinessUnitId,
    currentRole,
    features,
    isLoaded: isBUContextLoaded,
  } = useBusinessUnit();

  const [weekOffset, setWeekOffset] = useState(null);
  const selectedWeekRange = useMemo(() => getWeekRange(weekOffset ?? 0), [weekOffset]);

  const [chartGranularity, setChartGranularity] = useState("month");

  const [statistics, setStatistics] = useState(null);
  const [filterSourceStatistics, setFilterSourceStatistics] = useState(null);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const hasLoadedStatisticsRef = useRef(false);
  const isFetchingRef = useRef(false);

  // Partners KPI state
  const [partnersKpis, setPartnersKpis] = useState(null);
  const [partnersKpiPeriod, setPartnersKpiPeriod] = useState("month");

  const [filters, setFilters] = useState({
    businessUnitIds: [],
    personType: DASHBOARD_PERSON_TYPES.ALL,
    salesId: null,
    recruiterId: null,
    drilldown: {
      bucket: "month",
      year: null,
      month: null,
    },
  });

  const allBusinessUnitIds = useMemo(
    () => (businessUnits || []).map((businessUnit) => String(businessUnit.id)),
    [businessUnits]
  );
  const currentUserId = useMemo(() => getCurrentUserId(user), [user]);

  const userRole = useMemo(() => {
    const hasBusinessUnits = Boolean(businessUnits?.length);
    return convertLegacyRoleWithContext(user?.role, hasBusinessUnits);
  }, [businessUnits, user?.role]);

  const businessUnitRole = useMemo(() => convertLegacyRole(currentRole), [currentRole]);
  const canSeeTeam = useMemo(
    () => isCompanyAdmin(userRole) || isBUAdmin(businessUnitRole) || isManagerRole(businessUnitRole),
    [businessUnitRole, userRole]
  );

  const selfPersonType = useMemo(() => {
    if (isSalesRole(businessUnitRole)) return DASHBOARD_PERSON_TYPES.SALES;
    if (isRecruiterRole(businessUnitRole)) return DASHBOARD_PERSON_TYPES.RECRUITER;
    return DASHBOARD_PERSON_TYPES.ALL;
  }, [businessUnitRole]);

  const canUseMultiBusinessUnitFilter = canSeeTeam && allBusinessUnitIds.length > 1;
  const canUsePersonFilter = canSeeTeam;
  const canFetchDashboardStatistics = isBUContextLoaded && (filters.businessUnitIds.length > 0 || allBusinessUnitIds.length === 0);

  useEffect(() => {
    if (!isBUContextLoaded) return;

    setFilters((currentFilters) => {
      const nextBusinessUnitIds =
        currentFilters.businessUnitIds.filter((value) => allBusinessUnitIds.includes(String(value))) ||
        [];
      const businessUnitIds =
        nextBusinessUnitIds.length > 0
          ? nextBusinessUnitIds
          : selectedBusinessUnitId
          ? [String(selectedBusinessUnitId)]
          : allBusinessUnitIds.slice(0, 1);

      if (!canUsePersonFilter && currentUserId && selfPersonType !== DASHBOARD_PERSON_TYPES.ALL) {
        return {
          ...currentFilters,
          businessUnitIds,
          personType: selfPersonType,
          salesId: selfPersonType === DASHBOARD_PERSON_TYPES.SALES ? currentUserId : null,
          recruiterId:
            selfPersonType === DASHBOARD_PERSON_TYPES.RECRUITER ? currentUserId : null,
        };
      }

      return {
        ...currentFilters,
        businessUnitIds,
      };
    });
  }, [
    allBusinessUnitIds,
    canUsePersonFilter,
    currentUserId,
    isBUContextLoaded,
    selectedBusinessUnitId,
    selfPersonType,
  ]);

  const fetchStatistics = useCallback(
    async ({ silent = false } = {}) => {
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;

      const targetFilters = {
        businessUnitIds: filters.businessUnitIds,
        personType: filters.personType,
        salesId: filters.salesId,
        recruiterId: filters.recruiterId,
        granularity: chartGranularity,
      };

      if (!silent) {
        if (hasLoadedStatisticsRef.current) {
          setIsRefreshing(true);
        } else {
          setIsInitialLoading(true);
          setIsTableLoading(true);
        }
      }

      try {
        const payload = await getDashboardStatistics(targetFilters);
        hasLoadedStatisticsRef.current = true;
        setStatistics(payload || null);
      } catch (error) {
        if (!silent) {
          toast({
            title: t("dashboard.errors.fetchStats"),
            status: "error",
          });
        }
        setStatistics(null);
      } finally {
        isFetchingRef.current = false;
        if (!silent) {
          setIsInitialLoading(false);
          setIsRefreshing(false);
          setIsTableLoading(false);
        }
      }
    },
    [
      chartGranularity,
      filters.businessUnitIds,
      filters.personType,
      filters.recruiterId,
      filters.salesId,
      t,
      toast,
    ]
  );

  const fetchFilterSourceStatistics = useCallback(async () => {
    try {
      const payload = await getDashboardStatistics({
        businessUnitIds: filters.businessUnitIds,
      });
      setFilterSourceStatistics(payload || null);
    } catch (error) {
      setFilterSourceStatistics(null);
    }
  }, [filters.businessUnitIds]);

  useEffect(() => {
    if (!canFetchDashboardStatistics) return;
    fetchStatistics();
  }, [canFetchDashboardStatistics, fetchStatistics]);

  useEffect(() => {
    if (!canFetchDashboardStatistics) return;
    fetchFilterSourceStatistics();
  }, [canFetchDashboardStatistics, fetchFilterSourceStatistics]);

  useEffect(() => {
    if (!canFetchDashboardStatistics) return;
    const intervalId = window.setInterval(() => {
      fetchStatistics({ silent: true });
    }, 30000);
    return () => window.clearInterval(intervalId);
  }, [canFetchDashboardStatistics, fetchStatistics]);

  const monthBuckets = useMemo(() => {
    return buildMonthRange(statistics?.range_start, statistics?.range_end, i18n.language) || buildFallbackMonths(6, i18n.language);
  }, [i18n.language, statistics]);

  useEffect(() => {
    if (!monthBuckets.length) return;
    setFilters((currentFilters) => {
      const monthKey = currentFilters.drilldown.year && currentFilters.drilldown.month
        ? buildMonthKey(currentFilters.drilldown.year, currentFilters.drilldown.month)
        : null;
      const selectedBucket = monthBuckets.find((bucket) => bucket.key === monthKey) || monthBuckets[monthBuckets.length - 1];
      return {
        ...currentFilters,
        drilldown: {
          ...currentFilters.drilldown,
          year: selectedBucket.year,
          month: selectedBucket.month,
        },
      };
    });
  }, [monthBuckets]);

  const selectedBusinessUnitIds = filters.businessUnitIds.length
    ? filters.businessUnitIds
    : allBusinessUnitIds;

  const selectedBusinessUnits = useMemo(
    () =>
      (businessUnits || []).filter((businessUnit) =>
        selectedBusinessUnitIds.includes(String(businessUnit.id))
      ),
    [businessUnits, selectedBusinessUnitIds]
  );

  const hasCvSection = useMemo(() => {
    if (!selectedBusinessUnits.length) return Boolean(features?.has_cv);
    return selectedBusinessUnits.some((businessUnit) => Boolean(businessUnit?.has_cv));
  }, [features?.has_cv, selectedBusinessUnits]);

  const hasSkillsSection = useMemo(() => {
    if (!selectedBusinessUnits.length) return Boolean(features?.has_skills);
    return selectedBusinessUnits.some((businessUnit) => Boolean(businessUnit?.has_skills));
  }, [features?.has_skills, selectedBusinessUnits]);

  const hasPilotSection = useMemo(() => {
    if (!selectedBusinessUnits.length) {
      return Boolean(features?.has_pilot && features?.has_pilot_license);
    }
    return selectedBusinessUnits.some((businessUnit) => {
      const hasLicense =
        businessUnit?.has_pilot_license_for_user ??
        businessUnit?.pilot_license_enabled ??
        businessUnit?.has_pilot_license ??
        true;
      return Boolean(businessUnit?.has_pilot) && Boolean(hasLicense);
    });
  }, [features?.has_pilot, features?.has_pilot_license, selectedBusinessUnits]);

  const hasPartnersSection = useMemo(() => {
    if (!selectedBusinessUnits.length) return Boolean(features?.has_partners);
    return selectedBusinessUnits.some((businessUnit) => Boolean(businessUnit?.has_partners));
  }, [features?.has_partners, selectedBusinessUnits]);

  // Fetch Partners KPIs when partners section is active
  useEffect(() => {
    if (!hasPartnersSection || !isBUContextLoaded) return;
    const fetchPartnersKpis = async () => {
      try {
        const data = await getDashboardKpis({
          buId: selectedBusinessUnitId || undefined,
          period: partnersKpiPeriod,
        });
        setPartnersKpis(data);
      } catch {
        setPartnersKpis(null);
      }
    };
    fetchPartnersKpis();
  }, [hasPartnersSection, isBUContextLoaded, selectedBusinessUnitId, partnersKpiPeriod]);

  const seriesByMetric = useMemo(
    () => buildSeriesFromStatistics(statistics, selectedBusinessUnitIds, monthBuckets),
    [monthBuckets, selectedBusinessUnitIds, statistics]
  );

  const chartBuckets = useMemo(
    () => (chartGranularity === "week" ? buildWeekBuckets(12, i18n.language) : monthBuckets),
    [chartGranularity, i18n.language, monthBuckets]
  );

  const pilotSeriesByMetric = useMemo(
    () =>
      chartGranularity === "week"
        ? buildWeekSeriesFromStatistics(statistics, selectedBusinessUnitIds, chartBuckets)
        : buildSeriesFromStatistics(statistics, selectedBusinessUnitIds, chartBuckets),
    [chartBuckets, chartGranularity, selectedBusinessUnitIds, statistics]
  );

  const pilotChartData = useMemo(
    () => buildPilotChartData(pilotSeriesByMetric, chartBuckets),
    [chartBuckets, pilotSeriesByMetric]
  );

  const pilotDetailRows = useMemo(
    () => buildPilotDetailRows(statistics, selectedBusinessUnitIds, filters),
    [filters, selectedBusinessUnitIds, statistics]
  );

  const rankings = useMemo(
    () => buildRankings(statistics, selectedBusinessUnitIds, filters),
    [filters, selectedBusinessUnitIds, statistics]
  );

  const personOptions = useMemo(
    () =>
      buildPersonOptions({
        statistics: filterSourceStatistics || statistics,
        selectedBusinessUnitIds,
        fallbackUser: user,
        fallbackRole: selfPersonType,
      }),
    [filterSourceStatistics, selectedBusinessUnitIds, selfPersonType, statistics, user]
  );

  const selectedPersonValue = filters.salesId
    ? `${DASHBOARD_PERSON_TYPES.SALES}:${filters.salesId}`
    : filters.recruiterId
    ? `${DASHBOARD_PERSON_TYPES.RECRUITER}:${filters.recruiterId}`
    : DASHBOARD_PERSON_TYPES.ALL;

  const selectedMonthBucket =
    monthBuckets.find(
      (bucket) =>
        safeNumber(bucket.year) === safeNumber(filters.drilldown.year) &&
        safeNumber(bucket.month) === safeNumber(filters.drilldown.month)
    ) || monthBuckets[monthBuckets.length - 1];

  const canDrillDown = hasPilotSection && selectedBusinessUnitIds.length === 1;
  const drillDownHelpText = canDrillDown
    ? t("dashboard.drilldown.help")
    : t("dashboard.drilldown.disabledMultiBu");

  const handleBusinessUnitChange = (values) => {
    const normalizedValues = values.length ? values : [String(selectedBusinessUnitId)];
    setFilters((currentFilters) => ({
      ...currentFilters,
      businessUnitIds: normalizedValues.filter(Boolean),
    }));
  };

  const handlePersonChange = (value) => {
    if (value === DASHBOARD_PERSON_TYPES.ALL) {
      setFilters((currentFilters) => ({
        ...currentFilters,
        personType: DASHBOARD_PERSON_TYPES.ALL,
        salesId: null,
        recruiterId: null,
      }));
      return;
    }

    const [personType, id] = String(value).split(":");
    setFilters((currentFilters) => ({
      ...currentFilters,
      personType,
      salesId: personType === DASHBOARD_PERSON_TYPES.SALES ? id : null,
      recruiterId: personType === DASHBOARD_PERSON_TYPES.RECRUITER ? id : null,
    }));
  };

  const handleDrilldownMonthChange = (monthKey) => {
    const nextBucket = monthBuckets.find((bucket) => bucket.key === monthKey);
    if (!nextBucket) return;
    setFilters((currentFilters) => ({
      ...currentFilters,
      drilldown: {
        ...currentFilters.drilldown,
        year: nextBucket.year,
        month: nextBucket.month,
      },
    }));
  };

  const handleOpenDrilldown = () => {
    if (!canDrillDown || !selectedMonthBucket) return;
    const { fromDate, toDate } = buildWeekRangeForMonth(selectedMonthBucket);
    const query = new URLSearchParams({
      bucket: "week",
      fromDate,
      toDate,
    });

    if (filters.personType !== DASHBOARD_PERSON_TYPES.ALL) {
      query.set("roleScope", filters.personType);
    }

    if (filters.personType === DASHBOARD_PERSON_TYPES.SALES && filters.salesId) {
      query.set("userIds", String(filters.salesId));
    }

    if (filters.personType === DASHBOARD_PERSON_TYPES.RECRUITER && filters.recruiterId) {
      query.set("userIds", String(filters.recruiterId));
    }

    navigate(`/portal/asap-pilot/performance?${query.toString()}`, {
      state: {
        fromDashboard: true,
        returnTo: `${location.pathname}${location.search}`,
      },
    });
  };

  return (
    <Box mt="-24px" mx="-24px" mb="-24px" minH="100%">
      <PageHeader
        moduleCode="DB"
        eyebrow={t("dashboard.eyebrow", "ASAP Hub")}
        title={t("dashboard.title")}
      />

      <Box p="20px">
        <DashboardFilters
          t={t}
          isLoading={isRefreshing}
          businessUnits={businessUnits}
          selectedBusinessUnitIds={selectedBusinessUnitIds}
          allBusinessUnitIds={allBusinessUnitIds}
          singleBusinessUnitFallbackId={selectedBusinessUnitId ? String(selectedBusinessUnitId) : null}
          onBusinessUnitChange={handleBusinessUnitChange}
          canUseMultiBusinessUnitFilter={canUseMultiBusinessUnitFilter}
          personOptions={personOptions}
          selectedPersonValue={selectedPersonValue}
          onPersonChange={handlePersonChange}
          canUsePersonFilter={canUsePersonFilter}
          chartGranularity={chartGranularity}
          onGranularityChange={setChartGranularity}
        />

        {statistics?.range_start && statistics?.range_end && (
          <Text fontSize="12px" color="portal.muted" mb="12px" sx={{ marginBottom: "12px !important" }}>
            {t("dashboard.rangeLabel", {
              start: statistics.range_start,
              end: statistics.range_end,
            })}
          </Text>
        )}

        {features?.has_pilot_license && (
          <Box mb="12px">
            <MyTasksWidget buId={selectedBusinessUnitId} />
          </Box>
        )}

      {isInitialLoading ? (
        <Panel>
          <Center py="40px">
            <Spinner color="portal.primary" />
          </Center>
        </Panel>
      ) : !statistics ? (
        <Panel>
          <EmptyState title={t("dashboard.noData")} />
        </Panel>
      ) : (
        <Grid gap="12px">
          <SimpleGrid columns={{ base: 1, xl: 2 }} spacing="12px">
            {hasCvSection && (
              <ModuleVolumeCard
                title={t("dashboard.cards.cv.eyebrow")}
                subtitle={t("dashboard.cards.cv.subtitle")}
                totalLabel={t("dashboard.totalLabel", { value: sumSeries(pilotSeriesByMetric.cvSeries) })}
                series={pilotSeriesByMetric.cvSeries}
                color="#2C7A7B"
                emptyLabel={t("dashboard.cards.cv.empty")}
              />
            )}

            {hasSkillsSection && (
              <ModuleVolumeCard
                title={t("dashboard.cards.skills.eyebrow")}
                subtitle={t("dashboard.cards.skills.subtitle")}
                totalLabel={t("dashboard.totalLabel", {
                  value: sumSeries(pilotSeriesByMetric.skillsSeries),
                })}
                series={pilotSeriesByMetric.skillsSeries}
                color="#805AD5"
                emptyLabel={t("dashboard.cards.skills.empty")}
              />
            )}
          </SimpleGrid>

          {hasPilotSection && (
            <>
              <PilotGroupedChartCard
                t={t}
                isLoading={isRefreshing}
                chartData={pilotChartData}
                totals={{
                  needsCreated: sumSeries(pilotSeriesByMetric.needsCreatedSeries),
                  needsCovered: sumSeries(pilotSeriesByMetric.needsCoveredSeries),
                  matchesProposed: sumSeries(pilotSeriesByMetric.matchesProposedSeries),
                  matchesConfirmed: sumSeries(pilotSeriesByMetric.matchesConfirmedSeries),
                }}
                selectedMonthKey={selectedMonthBucket?.key || ""}
                onMonthChange={handleDrilldownMonthChange}
                onDrillDown={handleOpenDrilldown}
                isDrillDownDisabled={!canDrillDown}
                drillDownHelpText={drillDownHelpText}
                chartGranularity={chartGranularity}
              />

              <PilotDetailTableCard
                t={t}
                rows={pilotDetailRows}
                isLoading={isTableLoading}
                weekRange={selectedWeekRange}
                weekOffset={weekOffset}
                onPrevWeek={() => setWeekOffset((n) => (n ?? 0) - 1)}
                onNextWeek={() => setWeekOffset((n) => (n ?? 0) + 1)}
                onCurrentWeek={() => setWeekOffset(0)}
                onClearWeek={() => setWeekOffset(null)}
              />

              <SimpleGrid columns={{ base: 1, xl: 2 }} spacing="12px">
                <RankingPodiumCard
                  title={t("dashboard.podiums.recruiterTitle")}
                  items={rankings.recruiterRanking}
                  metricLabelBuilder={(item) =>
                    t("dashboard.podiums.recruiterMetric", {
                      count: safeNumber(item.placements),
                    })
                  }
                  emptyLabel={t("dashboard.podiums.empty")}
                />
                <RankingPodiumCard
                  title={t("dashboard.podiums.salesTitle")}
                  items={rankings.salesRanking}
                  metricLabelBuilder={(item) =>
                    t("dashboard.podiums.salesMetric", {
                      count: safeNumber(item.conversions),
                    })
                  }
                  emptyLabel={t("dashboard.podiums.empty")}
                />
              </SimpleGrid>
            </>
          )}

          {hasPartnersSection && (
            <Panel
              title={t("dashboard.cards.partners.eyebrow", "ASAP Partners")}
              actions={
                <ButtonGroup size="sm" isAttached variant="outline">
                  {["week", "month", "all"].map((p) => (
                    <Button
                      key={p}
                      h="28px"
                      px="10px"
                      fontSize="11px"
                      variant={partnersKpiPeriod === p ? "primary" : "secondary"}
                      onClick={() => setPartnersKpiPeriod(p)}
                    >
                      {t(`dashboard.cards.partners.period.${p}`, p === "week" ? "Week" : p === "month" ? "Month" : "All")}
                    </Button>
                  ))}
                </ButtonGroup>
              }
            >
              {partnersKpis ? (
                <Box p="12px">
                  <KpiStrip columns={{ base: 1, md: 3 }}>
                    <KpiCard
                      label={t("dashboard.cards.partners.jobPostingsToPool", "Job postings to candidates")}
                      value={partnersKpis.jobPostingsToPool?.count ?? 0}
                    />
                    <KpiCard
                      label={t("dashboard.cards.partners.jobPostingsToPartners", "Job postings to partners")}
                      value={partnersKpis.jobPostingsToPartners?.count ?? 0}
                      hint={
                        partnersKpis.jobPostingsToPartners?.signedRate != null
                          ? t("dashboard.cards.partners.signedRate", "Signed rate: {{rate}}%", { rate: partnersKpis.jobPostingsToPartners.signedRate })
                          : undefined
                      }
                    />
                    <KpiCard
                      label={t("dashboard.cards.partners.consultantsProposed", "Consultants proposed to partners")}
                      value={partnersKpis.consultantsProposedToPartners?.count ?? 0}
                      hint={
                        partnersKpis.consultantsProposedToPartners?.signedRate != null
                          ? t("dashboard.cards.partners.signedRate", "Signed rate: {{rate}}%", { rate: partnersKpis.consultantsProposedToPartners.signedRate })
                          : undefined
                      }
                    />
                  </KpiStrip>
                </Box>
              ) : (
                <EmptyState title={t("dashboard.cards.partners.noData", "No Partners data available.")} />
              )}
            </Panel>
          )}

          {!hasCvSection && !hasSkillsSection && !hasPilotSection && !hasPartnersSection && (
            <Panel>
              <EmptyState title={t("dashboard.noData")} />
            </Panel>
          )}
        </Grid>
      )}
      </Box>
    </Box>
  );
};

export default Dashboard; 
